import React, {useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {Box, Button, IconButton, Link, List, TextField, Tooltip, Typography} from "@mui/material";
import StopIcon from "@mui/icons-material/Stop";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ChatbotMessage from "../../components/chatbot/ChatbotMessage";
import DocumentAttachChip from "../../components/files/DocumentAttachChip";
import SubmitButton from "../../components/buttons/SubmitButton";
import useDocumentAttachAdornment from "./useDocumentAttachAdornment";
import SessionSharingDialog from "./SessionSharingDialog";
import {useRexAITrustedAdvisorContext} from "./Context/RexAITrustedAdvisorProvider";
import {useRexAITrustedAdvisorStyles} from "./useRexAITrustedAdvisorStyles";
import {useRexAI} from "../../context/RexAIProvider";
import PlaybookSelectorWidget from "../../components/chatbot/widgets/PlaybookSelectorWidget";
import {PLAYBOOK_WIDGET_TOOL_NAME} from "../../components/agent/useAgentListTools/toolNames";
import {map} from "underscore";

// Within this distance of the bottom, the list still follows new text.
const FOLLOW_LATEST_THRESHOLD_PX = 48;

const distanceFromListEnd = (list) => list.scrollHeight - list.scrollTop - list.clientHeight;

const isListAtBottom = (list) => distanceFromListEnd(list) <= FOLLOW_LATEST_THRESHOLD_PX;

const isListAtEnd = (list) => distanceFromListEnd(list) <= 1;

const MaestroConversation = ({inputId = "rex-ai-maestro-prompt-input"}) => {
  const {classes} = useRexAITrustedAdvisorStyles();
  const {handleWidgetSubmit, pendingWidgetMessage} = useRexAI();
  const [input, setInput] = useState("");
  const [sharingOpen, setSharingOpen] = useState(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const followLatestRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const messageListContentRef = useRef(null);
  const onInputChange = useCallback((e) => setInput(e.target.value), []);

  const {
    messages,
    error,
    loading,
    isThinking,
    isStopping,
    stoppedPromptToRestore,
    stoppedPromptRestoreLabel,
    isCurrentRequest,
    pendingAttachments,
    maxAttachments,
    pendingConfirmation,
    documentUploadEnabled,
    isDocUploading,
    handleAttach,
    handlePaste,
    handleSend,
    handleConfirmation,
    removeAttachment,
    stopGeneration,
    restoreStoppedPrompt,
    downloadDocument,
    messageListRef,
    inputRef,
    isReadOnly,
    sessionId,
    currentSession,
    fetchSessions,
    sharedConfirmationPending,
  } = useRexAITrustedAdvisorContext();

  const scrollToLatest = useCallback(() => {
    followLatestRef.current = true;
    setShowJumpToLatest(false);
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messageListRef]);

  // Scroll events arrive a frame late, after more content may have rendered, so distance alone
  // cannot tell the reader's scrolling from our own pins. Only an upward move stops following;
  // our pins only move down, and a shrink that clamps the list leaves it at the end.
  const onMessageListScroll = useCallback(() => {
    const list = messageListRef.current;
    if (!list) {
      return;
    }
    const scrolledUp = list.scrollTop < lastScrollTopRef.current && !isListAtEnd(list);
    lastScrollTopRef.current = list.scrollTop;
    if (scrolledUp) {
      followLatestRef.current = false;
    } else if (isListAtBottom(list)) {
      followLatestRef.current = true;
    }
    setShowJumpToLatest(!followLatestRef.current);
  }, [messageListRef]);

  // Content size, not message identity, drives following: a background refresh that rebuilds
  // the same messages must not move the view, and a reader who scrolled up is never moved.
  useEffect(() => {
    const list = messageListRef.current;
    const content = messageListContentRef.current;
    if (!list || !content) {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (followLatestRef.current) {
        list.scrollTop = list.scrollHeight;
      } else if (isListAtEnd(list)) {
        followLatestRef.current = true;
      }
      setShowJumpToLatest(!followLatestRef.current);
    });
    observer.observe(list);
    observer.observe(content);
    return () => observer.disconnect();
  }, [messageListRef]);

  // A newly opened session starts at its latest message; the observer settles the button.
  useEffect(() => {
    followLatestRef.current = true;
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [sessionId, messageListRef]);

  const onConfirmation = useCallback(
    (...args) => {
      scrollToLatest();
      return handleConfirmation(...args);
    },
    [handleConfirmation, scrollToLatest],
  );

  const lastMessage = messages[messages.length - 1];
  const activeWidgetSpec =
    !isReadOnly &&
    !sharedConfirmationPending &&
    !isCurrentRequest &&
    !pendingWidgetMessage &&
    lastMessage?.sender === "assistant" ?
      lastMessage.pendingWidget :
      null;
  const showPlaybookWidget = activeWidgetSpec?.type === PLAYBOOK_WIDGET_TOOL_NAME;

  let sharingHelperText;
  if (isReadOnly) {
    sharingHelperText = (
      <Link component="button" type="button" onClick={() => setSharingOpen(true)}>
        This session was shared with you — view only.
      </Link>
    );
  } else if (currentSession?.hasActiveParticipants) {
    sharingHelperText = (
      <Link component="button" type="button" onClick={() => setSharingOpen(true)}>
        This session is shared.
      </Link>
    );
  }

  const attachInputProps = useDocumentAttachAdornment({
    documentUploadEnabled,
    handleAttach,
    isReadOnly,
    isCurrentRequest,
    isStopping,
    pendingConfirmation,
    pendingAttachments,
    maxAttachments,
  });

  // Mirrors handleSend's early returns so a rejected send leaves the reader's place alone.
  const willSend = !!input.trim() && !isCurrentRequest;

  const onFormSubmit = useCallback(
    (e) => {
      if (willSend) {
        scrollToLatest();
      }
      return handleSend(e, input, setInput);
    },
    [handleSend, input, scrollToLatest, willSend],
  );

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey && !isDocUploading) {
        e.preventDefault();
        if (willSend) {
          scrollToLatest();
        }
        handleSend(e, input, setInput);
      }
    },
    [handleSend, input, isDocUploading, scrollToLatest, willSend],
  );

  const onPaste = documentUploadEnabled && !isReadOnly ? handlePaste : undefined;
  let promptPlaceholder = "Ask Maestro about incident response...";
  if (pendingConfirmation) {
    promptPlaceholder = "Type \"y\" to approve or \"n\" to cancel...";
  }

  return (
    <Box className={classes.conversation}>
      <Box className={classes.messageListArea}>
        <List
          ref={messageListRef}
          id="rex-ai-maestro-message-list"
          className={classes.messageList}
          onScroll={onMessageListScroll}
        >
          <Box
            ref={messageListContentRef}
            id="rex-ai-maestro-message-list-wrapper"
            className={classes.messageListBox}
          >
            {map(messages, (message, index) => (
              <ChatbotMessage
                key={`${message.messageId}-${index}`}
                message={message}
                isStreaming={isCurrentRequest && index === messages.length - 1}
                attachments={message.attachments}
                onDownloadDocument={downloadDocument}
              />
            ))}
            {pendingConfirmation?.confirmation && (
              <ChatbotMessage
                key={`confirmation-${pendingConfirmation.confirmationId}`}
                message={{
                  messageId: `confirmation-${pendingConfirmation.confirmationId}`,
                  text: "_Working on platform request..._",
                  sender: "assistant",
                  ephemeral: true,
                }}
                pendingConfirmation={pendingConfirmation}
                onConfirmation={onConfirmation}
              />
            )}
            {sharedConfirmationPending && (
              <ChatbotMessage
                key="shared-confirmation-pending"
                message={{
                  messageId: "shared-confirmation-pending",
                  text: "_An operation is awaiting confirmation from the session owner._",
                  sender: "assistant",
                  ephemeral: true,
                }}
              />
            )}
            {isThinking && (
              <ChatbotMessage
                key="thinking-indicator"
                message={{
                  messageId: "thinking",
                  text: "_Thinking..._",
                  sender: "assistant",
                  ephemeral: true,
                }}
              />
            )}
            {showPlaybookWidget && (
              <Box
                id="rex-ai-maestro-widget-bubble"
                px={2}
                py={1}
                display="flex"
                justifyContent="flex-end"
              >
                <PlaybookSelectorWidget
                  payload={activeWidgetSpec.payload}
                  onSubmit={(value, label) => {
                    scrollToLatest();
                    handleWidgetSubmit(value, activeWidgetSpec, label);
                  }}
                />
              </Box>
            )}
          </Box>
        </List>
        {showJumpToLatest && (
          <Button
            id="rex-ai-maestro-jump-to-latest"
            variant="contained"
            size="small"
            startIcon={<ArrowDownwardIcon />}
            onClick={scrollToLatest}
            className={classes.jumpToLatest}
          >
            Jump to latest
          </Button>
        )}
      </Box>

      {error && (
        <Typography color="error" align="center" className={classes.messageListError}>
          {error}
          {stoppedPromptToRestore && (
            <>
              {" "}
              <Link component="button" type="button" onClick={restoreStoppedPrompt}>
                {stoppedPromptRestoreLabel}
              </Link>
            </>
          )}
        </Typography>
      )}

      <Box component="form" onSubmit={onFormSubmit} className={classes.formBox}>
        <Box className={classes.formBoxColumn}>
          <Box className={classes.chipArea}>
            {map(pendingAttachments, (pendingAttachment) => (
              <DocumentAttachChip
                key={pendingAttachment.id}
                attachment={pendingAttachment}
                onRemove={removeAttachment}
                maxFiles={maxAttachments}
              />
            ))}
          </Box>
          <Box className={classes.formBoxWrapper}>
            <TextField
              inputRef={inputRef}
              id={inputId}
              fullWidth
              variant="outlined"
              placeholder={promptPlaceholder}
              value={input}
              onChange={onInputChange}
              multiline
              maxRows={4}
              helperText={sharingHelperText}
              disabled={isReadOnly}
              InputProps={attachInputProps}
              onPaste={onPaste}
              onKeyDown={onKeyDown}
            />
            {!isReadOnly &&
              (isCurrentRequest ?
                (
                  <Tooltip title="Stop conversation">
                    <IconButton
                      id="rex-ai-maestro-stop"
                      onClick={stopGeneration}
                      disabled={isStopping}
                      color="error"
                      className={classes.stopGeneration}
                    >
                      <StopIcon />
                    </IconButton>
                  </Tooltip>
                ) :
                (
                  <SubmitButton
                    id="rex-ai-maestro-send"
                    onSubmit={onFormSubmit}
                    disabled={isCurrentRequest || isStopping || isDocUploading || !input.trim()}
                    isLoading={loading}
                    inline
                  >
                    Send
                  </SubmitButton>
                ))}
          </Box>
        </Box>
      </Box>
      {sharingOpen && currentSession && (
        <SessionSharingDialog
          open
          session={currentSession}
          readOnly={isReadOnly}
          onClose={() => setSharingOpen(false)}
          onChanged={fetchSessions}
        />
      )}
    </Box>
  );
};

MaestroConversation.propTypes = {
  inputId: PropTypes.string,
};

export default MaestroConversation;
